import React, { useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import { SiteCamerasResult } from "../../Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { SiteWallCamerasStatus } from "./useSiteWallCameras";
import {
  EQUAL_GRID_PAGE_SIZE,
  FOCUS_GRID_SIDE_PAGE_SIZE,
  VIDEO_FRAME_CLASS,
  VIDEO_FRAME_ROUNDED_CLASS,
} from "./siteVideoWallConstants";
import { SiteVideoWallPagination } from "./SiteVideoWallPagination";
import { SiteVideoWallTile } from "./SiteVideoWallTile";
import { WallIdlePrompt } from "./WallStreams";

export type SiteVideoWallFullMode = "focus" | "equal";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface SiteVideoWallFullDialogProps {
  open: boolean;
  onClose: () => void;
  siteName: string;
  alertContextLabel: string;
  status: SiteWallCamerasStatus;
  cameras: SiteCamerasResult[];
  totalCount: number;
  mode: SiteVideoWallFullMode;
  onModeChange: (mode: SiteVideoWallFullMode) => void;
  page: number;
  onPageChange: (page: number) => void;
  focusedCameraId: string | null;
  onFocusedCameraChange: (cameraId: string) => void;
  ensureCamerasLoaded: (upToIndex: number) => Promise<void>;
  closeLabel?: string;
}

export const SiteVideoWallFullDialog: React.FC<SiteVideoWallFullDialogProps> = ({
  open,
  onClose,
  siteName,
  alertContextLabel,
  status,
  cameras,
  totalCount,
  mode,
  onModeChange,
  page,
  onPageChange,
  focusedCameraId,
  onFocusedCameraChange,
  ensureCamerasLoaded,
  closeLabel = "Back to Alert",
}) => {
  const focusedCamera = cameras.find(
    (c) => String(c.id) === focusedCameraId
  );

  const focusSideTotalPages = Math.max(
    1,
    Math.ceil(totalCount / FOCUS_GRID_SIDE_PAGE_SIZE)
  );

  const equalTotalPages = Math.max(
    1,
    Math.ceil(totalCount / EQUAL_GRID_PAGE_SIZE)
  );

  const focusSideStart = page * FOCUS_GRID_SIDE_PAGE_SIZE;
  const focusSideEnd = focusSideStart + FOCUS_GRID_SIDE_PAGE_SIZE - 1;

  const equalStart = page * EQUAL_GRID_PAGE_SIZE;
  const equalEnd = equalStart + EQUAL_GRID_PAGE_SIZE - 1;

  useEffect(() => {
    if (!open || status !== "ready") return;

    if (mode === "focus") {
      ensureCamerasLoaded(focusSideEnd);
    } else {
      ensureCamerasLoaded(equalEnd);
    }
  }, [
    open,
    status,
    mode,
    page,
    focusSideEnd,
    equalEnd,
    ensureCamerasLoaded,
  ]);

  const sideCameras =
    mode === "focus"
      ? cameras.slice(focusSideStart, focusSideStart + FOCUS_GRID_SIDE_PAGE_SIZE)
      : [];

  const equalCameras =
    mode === "equal"
      ? cameras.slice(equalStart, equalStart + EQUAL_GRID_PAGE_SIZE)
      : [];

  const equalPlaceholderCount =
    mode === "equal"
      ? Math.max(
          0,
          Math.min(EQUAL_GRID_PAGE_SIZE, totalCount - equalStart) -
            equalCameras.length
        )
      : 0;

  const equalGridCols =
    totalCount <= 2 ? 2 : totalCount <= 6 ? 3 : 4;

  const equalGridTileCount = Math.min(
    EQUAL_GRID_PAGE_SIZE,
    Math.max(equalCameras.length + equalPlaceholderCount, 1)
  );
  const equalGridRows = Math.ceil(equalGridTileCount / equalGridCols);

  // Always reserve 3 rows so 4–6 camera pages don't stretch two visible rows
  // to fill the full panel height; empty row tracks stay blank (no placeholder tiles).
  const focusSideRows = 3;

  const paginationLabel =
    mode === "focus" && totalCount > FOCUS_GRID_SIDE_PAGE_SIZE
      ? `Cameras ${focusSideStart + 1}–${Math.min(
          focusSideStart + FOCUS_GRID_SIDE_PAGE_SIZE,
          totalCount
        )} of ${totalCount}`
      : undefined;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      data-testid="site-video-wall-full-dialog"
    >
      <div className="flex h-full flex-col gap-2.5 bg-white p-3.5 font-mulish md:p-4">
        <div className="flex flex-none flex-wrap items-center gap-3">
          <span className="text-[18px] font-extrabold uppercase tracking-[0.8px] text-actuate-blue">
            Site Video Wall — {siteName}
          </span>
          <span className="rounded border border-actuate-grey bg-[#FAFAFB] px-3 py-1 text-[14px] text-gray-600">
            Reviewing:{" "}
            <b className="font-bold text-actuate-blue">{alertContextLabel}</b>
          </span>

          <div className="shadow-button ml-auto flex overflow-hidden rounded border-2 border-actuate-orange">
            <button
              type="button"
              onClick={() => onModeChange("focus")}
              className={`border-r border-actuate-orange p-1.5 font-mulish text-base ${
                mode === "focus"
                  ? "bg-actuate-orange text-white hover:bg-[#E67300]"
                  : "bg-white text-actuate-orange"
              }`}
            >
              Focus + Grid
            </button>
            <button
              type="button"
              onClick={() => onModeChange("equal")}
              className={`p-1.5 font-mulish text-base ${
                mode === "equal"
                  ? "bg-actuate-orange text-white hover:bg-[#E67300]"
                  : "bg-white text-actuate-orange"
              }`}
            >
              Equal Grid
            </button>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="shadow-button rounded border-2 border-actuate-orange bg-actuate-orange p-1.5 font-mulish text-base text-white hover:bg-[#E67300]"
          >
            {closeLabel}
          </button>
        </div>

        <WallIdlePrompt />

        {status === "loading" && (
          <div className="flex flex-1 items-center justify-center text-gray-500">
            Retrieving cameras...
          </div>
        )}

        {status === "error" && (
          <div className="flex flex-1 items-center justify-center text-gray-500">
            Unable to load cameras for this site.
          </div>
        )}

        {status === "ready" && totalCount === 0 && (
          <div className="flex flex-1 items-center justify-center text-gray-500">
            No cameras found for this site.
          </div>
        )}

        {status === "ready" && totalCount > 0 && mode === "focus" && (
          <div className="flex min-h-0 flex-1 gap-2.5 overflow-hidden">
            <div className="flex min-h-0 min-w-0 flex-[2.4] items-center justify-center">
              {focusedCamera ? (
                <SiteVideoWallTile
                  cameraId={String(focusedCamera.id)}
                  cameraName={focusedCamera.camera_name}
                  enabled={open}
                  isFocused
                  compact={false}
                  showControls
                  fitToContainer
                  className="h-full w-full"
                />
              ) : (
                <div className={`flex aspect-[4/3] max-h-full w-full max-w-full items-center justify-center ${VIDEO_FRAME_ROUNDED_CLASS} border border-actuate-grey bg-[#FAFAFB] text-gray-500`}>
                  Select a camera
                </div>
              )}
            </div>
            <div className="flex min-h-0 min-w-0 flex-[1.6] flex-col gap-2">
              <div
                className="grid min-h-0 flex-1 grid-cols-3 gap-2"
                data-testid="focus-side-grid"
                style={{
                  gridTemplateRows: `repeat(${focusSideRows}, minmax(0, 1fr))`,
                }}
              >
                {sideCameras.map((camera) => {
                  const isSelected = String(camera.id) === focusedCameraId;

                  return (
                    <SiteVideoWallTile
                      key={camera.id}
                      cameraId={String(camera.id)}
                      cameraName={camera.camera_name}
                      enabled={open}
                      isFocused={isSelected}
                      onClick={() => onFocusedCameraChange(String(camera.id))}
                      placeholderMessage={
                        isSelected ? "Displaying video on left" : undefined
                      }
                      fitToContainer
                    />
                  );
                })}
              </div>
              <SiteVideoWallPagination
                page={page}
                totalPages={focusSideTotalPages}
                onPageChange={onPageChange}
                label={paginationLabel}
                className="flex-none justify-center py-1"
                variant="light"
                size="xlarge"
              />
            </div>
          </div>
        )}

        {status === "ready" && totalCount > 0 && mode === "equal" && (
          <div
            className="grid min-h-0 flex-1 gap-2"
            style={{
              gridTemplateColumns: `repeat(${equalGridCols}, minmax(0, 1fr))`,
              gridTemplateRows: `repeat(${equalGridRows}, minmax(0, 1fr))`,
            }}
          >
            {equalCameras.map((camera) => (
              <SiteVideoWallTile
                key={camera.id}
                cameraId={String(camera.id)}
                cameraName={camera.camera_name}
                enabled={open}
                isFocused={String(camera.id) === focusedCameraId}
                onClick={() => onFocusedCameraChange(String(camera.id))}
                showControls
                fitToContainer
              />
            ))}
            {Array.from({ length: equalPlaceholderCount }).map((_, i) => (
              <div
                key={`equal-placeholder-${i}`}
                className="flex min-h-0 min-w-0 items-center justify-center"
              >
                <div className={`aspect-[4/3] max-h-full w-full max-w-full ${VIDEO_FRAME_CLASS} border-transparent`} />
              </div>
            ))}
          </div>
        )}

        {status === "ready" && totalCount > 0 && mode === "equal" && (
          <SiteVideoWallPagination
            page={page}
            totalPages={equalTotalPages}
            onPageChange={onPageChange}
            className="flex-none justify-center py-2"
            variant="light"
            size="xlarge"
          />
        )}
      </div>
    </Dialog>
  );
};
